/** Markup parsing and allocation for the estimate ETL. */
import { Markup, MarkupData } from "../../interfaces/Markup";
import { Alternate } from "../../interfaces/Alternate";
import { LineItem } from "../../interfaces/LineItem";
import { EstimateUseGroup } from "../../interfaces/EstimateUseGroup";

export function parseMarkup(
  data: MarkupData,
  markups: MarkupData[],
  alternates: Alternate[],
  estimate_stats: any,
  useGroups?: { [key: string]: EstimateUseGroup },
  floorGroups?: any[]
): Markup {
  const alternate = alternates.find((alt) => alt.id === data.alternate) || null;

  // Format the 'order' property to a 4-digit string so excel functions sort it correctly
  const formattedOrder = data.order.toString().padStart(4, "0");
  function calculateAllAboveMarkups(): number {
    const aboveMarkups = markups
      .filter((y) => y.order < data.order)
      .map((y) => parseMarkup(y, markups, alternates, estimate_stats, useGroups, floorGroups).total);
    return aboveMarkups.reduce((total, m) => total + m, 0);
  }

  const total = data.formula
    ? data.formula.overridden
      ? data.formula.total
      : data.formula.total * (data.percentage / 100)
    : (estimate_stats.costOfTradesSubtotal - (estimate_stats.markupsTotal || 0) + calculateAllAboveMarkups()) *
      (data.percentage / 100);

  const percentage = data.formula?.overridden ? 0 : data.percentage;

  const alternate_desc = alternate ? alternate.label || "" : "";
  const alternate_code = alternate ? alternate.key || "" : "";

  function useGroupsLabel(): string {
    if (!useGroups) return "";

    // If estimate_use_groups is null, undefined, or empty, return all use groups
    if (!data.estimate_use_groups || Object.keys(data.estimate_use_groups).length === 0) {
      const allLabels = Object.values(useGroups)
        .sort((a, b) => a.order - b.order)
        .map((use) => (use.label && use.label !== "" ? use.label : use.use_group));
      return allLabels.join(", ");
    }

    // Otherwise, return only the specific use groups from estimate_use_groups
    const labels = Object.keys(data.estimate_use_groups)
      .map((key) => useGroups[key])
      .filter((use): use is EstimateUseGroup => use !== undefined)
      .sort((a, b) => a.order - b.order)
      .map((use) => (use.label && use.label !== "" ? use.label : use.use_group));

    return labels.join(", ");
  }

  function floorGroupsLabel(): string {
    if (!floorGroups || !useGroups) return "";

    // If estimate_use_groups is null, undefined, or empty, return all floor groups
    if (!data.estimate_use_groups || Object.keys(data.estimate_use_groups).length === 0) {
      const allLabels = Object.values(useGroups).map((use) => {
        const fg = floorGroups.find((fg) => fg.id === use.floor_group);
        return fg ? (fg.label && fg.label !== "" ? fg.label : fg.label) : "";
      });
      const uniqueLabels = Array.from(new Set(allLabels))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      return uniqueLabels.join(", ");
    }

    // Otherwise, return floor groups for the specific use groups from estimate_use_groups
    const labels = Object.keys(data.estimate_use_groups)
      .map((key) => useGroups[key])
      .filter((use): use is EstimateUseGroup => use !== undefined)
      .map((use) => {
        const fg = floorGroups.find((fg) => fg.id === use.floor_group);
        return fg ? (fg.label && fg.label !== "" ? fg.label : fg.label) : "";
      });

    const uniqueLabels = Array.from(new Set(labels))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return uniqueLabels.join(", ");
  }

  return {
    ...data,
    order: formattedOrder, // Update the 'order' property
    total: total || 0,
    percentage: percentage,
    alternateId: alternate?.id || "",
    alternate: alternate_code ? alternate_code + " - " + alternate_desc : "",
    alternate_desc,
    alternate_code,
    alternateStatus: alternate?.status || "",
    accepted: alternate?.accepted ?? true,
    use_group: useGroupsLabel(),
    floor_group: floorGroupsLabel(),
  };
}

/**
 * Calculate allocated markups for line items based on ATL Markups logic
 * Modifies line items directly by adding markup_allocation and total_plus_markup properties
 */

export const calculateAllocatedMarkups = (lineItems: LineItem[], markups: Markup[], alternates: Alternate[]): void => {
  // Initialize markup allocation to 0 for all line items and
  //initialize total_plus_markup to the total of the line item
  lineItems.forEach((item) => {
    item.markup_allocation = 0;
    item.total_plus_markup = Number(item.total) || 0;
  });

  const allocateMarkupToLineItems = (markup: Markup, allValidLineItems: LineItem[]) => {
    if (!markup.allocation || markup.allocation.total === 0 || allValidLineItems.length === 0) {
      return;
    }

    // Determine which line items to allocate to
    const targetLineItems: LineItem[] = [];

    if (markup.allocation.type === "all") {
      targetLineItems.push(...allValidLineItems);
    } else if (markup.allocation.type === "sortCodeList") {
      if (markup.allocation.sort_codes) {
        const sortCodes = markup?.allocation?.sort_codes || {};
        allValidLineItems.forEach((item) => {
          for (const [sortFieldId, sortCodeIds] of Object.entries(sortCodes)) {
            const itemSortCodeId = item.extras?.[sortFieldId];
            if (itemSortCodeId && sortCodeIds.includes(itemSortCodeId)) {
              targetLineItems.push(item);
            }
          }
        });
      }
      if (markup.allocation.uf) {
        const ufLevels = markup?.allocation?.uf || {};
        for (const [ufLevel, ufLevelCodes] of Object.entries(ufLevels)) {
          allValidLineItems.forEach((item) => {
            const itemUfLevelCode = item?.[ufLevel];
            if (itemUfLevelCode && ufLevelCodes.includes(itemUfLevelCode)) {
              targetLineItems.push(item);
            }
          });
        }
      }
      if (markup.allocation.mf) {
        const mfLevels = markup?.allocation?.mf || {};
        for (const [mfLevel, mfLevelCodes] of Object.entries(mfLevels)) {
          allValidLineItems.forEach((item) => {
            const itemMfLevelCode = item?.[mfLevel];
            if (itemMfLevelCode && mfLevelCodes.includes(itemMfLevelCode)) {
              targetLineItems.push(item);
            }
          });
        }
      }
    }
    if (targetLineItems.length === 0) {
      return;
    }

    // Calculate the total of all target line items
    const lineItemGroupTotal = targetLineItems.reduce((sum, item) => {
      const itemTotal = typeof item.total === "string" ? 0 : (item.total as number) || 0;
      return sum + itemTotal;
    }, 0);

    if (lineItemGroupTotal === 0) {
      // if the total of the line items is 0, there is no place to allocate the markup
      // so we remove the allocation and treat it as a normal markup
      markup.allocation = null;
      return;
    }

    // Allocate the markup proportionally to each target line item
    for (const lineItem of targetLineItems) {
      const itemTotal = typeof lineItem.total === "string" ? 0 : (lineItem.total as number) || 0;
      const allocation = (itemTotal / lineItemGroupTotal) * (markup.total ?? 0);
      lineItem.markup_allocation = (lineItem.markup_allocation || 0) + allocation;
      lineItem.total_plus_markup = (Number(lineItem.total) || 0) + lineItem.markup_allocation;
    }
  };

  // Filter base bid line items: no parent and either no alternate or alternate is accepted
  const baseBidLineItems = lineItems.filter(
    (item) =>
      !item.parent && // parent is null
      (!item.alternate || alternates.find((alt) => alt.id === item.alternateId)?.accepted) //include accepted alternates
  );

  // Process all markups
  for (const markup of markups) {
    if (!markup.alternateId) {
      // Base bid markup - allocate to base bid line items
      allocateMarkupToLineItems(markup, baseBidLineItems);
    } else {
      // Alternate markup - check if alternate is not accepted
      const alternate = alternates.find((alt) => alt.id === markup.alternateId);
      if (alternate && !alternate.accepted) {
        // Get line items that belong to this specific alternate and have no parent
        const alternateLineItems = lineItems.filter((item) => !item.parent && item.alternate === markup.alternateId);
        allocateMarkupToLineItems(markup, alternateLineItems);
      }
    }
  }

  // Process child line items
  // First create map of parent line item markup factors
  const markupFactorMap = new Map<string, number>();
  lineItems
    .filter((item) => !item.parent)
    .forEach((item) => {
      const total = Number(item.total) || 0;
      if (total > 0) {
        markupFactorMap.set(item.id, item.total_plus_markup / total);
      }
    });

  // Then use the parent markupFactor to calculate the child line items
  for (const lineItem of lineItems) {
    if (lineItem.parent) {
      const total = Number(lineItem.total) || 0;
      const markupFactor = markupFactorMap.get(lineItem.parent) || 1;
      lineItem.total_plus_markup = total * markupFactor;
      lineItem.markup_allocation = total * (markupFactor - 1);
    }
  }
};
